import React, { useEffect, useState } from 'react';
import { Table, Modal, Breadcrumb, Button, Card, Row, Col, Tag, Input } from 'antd';
import { HomeOutlined, EyeOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { Link } from 'react-router-dom';
import axios from 'axios';
import useUserProfile from '../../useUserProfile';
import { Tooltip } from 'antd'; // Import Tooltip from Ant Design
import { useTranslation } from 'react-i18next';

interface Nomination {
  nominationId: string;
  nominatorName: string;
  nomineeNames: string[];
  rewardName: string;
  rewardCategory: string;
  approvalStatus: string;
  approvalComments: string;
  unit: string;
}

interface NominationDetails {
  nominationId: string;
  nominatorName: string;
  nominatorId: string;
  nomineeNames: string[];
  nomineeIds: string[];
  rewardName: string;
  rewardCategory: string;
  budget: number;
  approvalStatus: string;
  approvalComments: string;
  nominationDate: string;
  nominationMonth: string;
  nominationYear: number;
  approverLevel1: string;
  approverLevel2: string;
  valueDemonstrated: string;
  ecardName: string;
  ecardImage: string;
  rewardTestimonial: string;
}

const ManagerActionsComponent: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [nominations, setNominations] = useState<Nomination[]>([]);
  const [selectedNomination, setSelectedNomination] = useState<NominationDetails | null>(null);
  const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
  const [awardsCount, setAwardsCount] = useState(0);
  const [awardsUsed, setAwardsUsed] = useState(0);
  const [rejectNominationId, setRejectNominationId] = useState<string | null>(null);
  const { requestProfileData } = useUserProfile();
  const [UserDetails, setUserDetails] = useState<{ id: string, displayName: string, email: string }[]>([]);
  const { t } = useTranslation();

  const { instance, accounts } = useMsal();

  const fetchToken = async () => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    });
    return res.idToken;
  };

  useEffect(() => {
    fetchToken();
  }, []);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        await requestProfileData();
        const userMail = localStorage.getItem("UserMail");
        const userName = localStorage.getItem("UserName");
        const userId = localStorage.getItem("UserId");
        setUserDetails([{ id: userId || '', displayName: userName || 'Default Nominator', email: userMail || '' }]);
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    fetchProfileData();
  }, [requestProfileData]);

  const fetchNominations = async () => {
    try {
      const token = await fetchToken();
      const Managerid = UserDetails[0]?.id;
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Approvals/manager/${Managerid}/simple`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const nominationsWithIndex = response.data.map((nomination: Nomination, index: number) => ({
        ...nomination,
        index: index + 1,
      }));
      setNominations(nominationsWithIndex);
    } catch (error) {
      console.error("Error fetching nominations", error);
    }
  };

  const fetchAwardsData = async () => {
    try {
      const token = await fetchToken();
      const Managerid = UserDetails[0]?.id;
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Approvals/UnitManagerBudget/${Managerid}`, {
        headers: {
          'Accept': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      setAwardsCount(response.data.awardsCount);
      setAwardsUsed(response.data.awardsUsed);
    } catch (error) {
      console.error("Error fetching awards data", error);
    }
  };

  useEffect(() => {
    if (UserDetails.length > 0 && UserDetails[0]?.id) {
      fetchNominations();
      fetchAwardsData();
    }
  }, [UserDetails]);

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const handleRejectModalClose = () => {
    setIsRejectModalVisible(false);
    setRejectNominationId(null);
  };

  const handleViewNomination = async (nominationId: string) => {
    try {
      const token = await fetchToken();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Approvals/nomination/${nominationId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data && response.data.length > 0) {
        setSelectedNomination(response.data[0]);
      }
      setIsModalVisible(true);
    } catch (error) {
      console.error("Error fetching nomination details", error);
    }
  };

  const extractCitation = (description: string): string => {
    const citationRegex = /~citation~([\s\S]*?)~FormFields~|~citation~([\s\S]*)/;
    const match = description.match(citationRegex);
    return match ? (match[1] || match[2] || '').trim() : description;
  };

  const handleSubmitNomination = async (nominationId: string, action: 'approve' | 'reject') => {
    try {
      const token = await fetchToken();
      await axios.post(`${process.env.REACT_APP_API_URL}/api/Approvals/action/${nominationId}`, {
        approvalStatus: action === 'approve' ? "Approved" : "Rejected",
        approvalComments: `${action.charAt(0).toUpperCase() + action.slice(1)} by manager`
      },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      fetchNominations();
      fetchAwardsData();
      setIsModalVisible(false);
      setIsRejectModalVisible(false);
    } catch (error) {
      console.error(`Error ${action}ing nomination`, error);
    }
  };

  const columns = [
    {
      title: t('No.'),
      dataIndex: 'index',
      key: 'index',
    },
    {
      title: t('reward_details'),
      dataIndex: 'rewardDetails',
      key: 'rewardDetails',
      render: (_: any, record: Nomination) => `${record.rewardCategory} - ${record.rewardName}`,
    },
    {
      title: t('nominee'),
      dataIndex: 'nomineeNames',
      key: 'nomineeNames',
      render: (nomineeNames: string[]) => nomineeNames?.join(', ') || 'N/A',
    },
    {
      title: t('nominator'),
      dataIndex: 'nominatorName',
      key: 'nominatorName',
    },
    {
      title: t('status'),
      dataIndex: 'approvalStatus',
      key: 'approvalStatus',
      render: (approvalStatus: string) => (
        <div style={{ textAlign: 'center' }}>
          <Tag color={approvalStatus === 'Approved' ? 'green' : approvalStatus === 'Rejected' ? 'red' : 'blue'}>
            {approvalStatus}
          </Tag>
        </div>
      ),
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (_: any, record: Nomination) => (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button icon={<EyeOutlined />} onClick={() => handleViewNomination(record.nominationId)}>View</Button>
          {record.approvalStatus === 'Approved' ? (
            <Button type="primary" disabled style={{ backgroundColor: ' lite green', borderColor: 'green', color: 'green' }}>
              Approved
            </Button>
          ) : (
            <Tooltip
              title={
                record.approvalStatus !== 'Pending'
                  ? 'Action not allowed'
                  : Math.floor(awardsUsed) >= Math.floor(awardsCount)
                    ? 'No budget to approve'
                    : ''
              }
            >
              <Button
                icon={<CheckOutlined />}
                type="primary"
                disabled={record.approvalStatus !== 'Pending' || Math.floor(awardsUsed) >= Math.floor(awardsCount)}
                onClick={() => handleSubmitNomination(record.nominationId, 'approve')}
              >
                Approve
              </Button>
            </Tooltip>
          )}
          <Button
            icon={<CloseOutlined />}
            danger
            disabled={record.approvalStatus !== 'Pending'}
            onClick={() => {
              setRejectNominationId(record.nominationId);
              setIsRejectModalVisible(true);
            }}
          >
            Reject
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: '10px', backgroundColor: 'white' }}>
      <div style={{ backgroundColor: 'white', padding: '0', display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }}>
        <div style={{ flex: '1' }}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/home">
                <HomeOutlined style={{ marginRight: '5px' }} />
                {t('home')}
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{t('manager_actions')}</Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>

      <h2 style={{ margin: '24px 0px 11px 0px', fontSize: '24px' }}>{t('manager_action_description')}</h2>

      <div style={{ marginBottom: '20px' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: '10px',
            boxShadow: '0 1px 1px rgba(215, 219, 219, 0.9)',
            borderRadius: '8px',
            overflow: 'hidden',
            margin: 0,
            padding: 0,
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5', textAlign: 'center' }}>
              <th
                style={{
                  width: '33%',
                  padding: '12px',
                  borderBottom: '1px solid #ddd',
                  borderRight: '1px solid #ddd',

                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: 'black',
                }}
              >
                {t('manager_action_awards')}
              </th>
              <th
                style={{
                  width: '33%',
                  padding: '12px',
                  borderBottom: '1px solid #ddd',
                  borderRight: '1px solid #ddd',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: 'black',
                }}
              >
                {t('manager_action_approved')}
              </th>
              <th
                style={{
                  width: '33%',
                  padding: '12px',
                  borderBottom: '1px solid #ddd',
                  borderRight: '1px solid #ddd',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: 'black',
                }}
              >
                {t('balance')}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ backgroundColor: '#ffffff', textAlign: 'center' }}>
              <td
                style={{
                  padding: '10px',
                  borderBottom: '1px solid #ddd',
                  borderRight: '1px solid #ddd',
                  fontSize: '14px',
                  color: '#262626',
                }}
              >
                <strong>{Math.floor(awardsCount)}</strong>
              </td>
              <td
                style={{
                  padding: '10px',
                  borderBottom: '1px solid #ddd',
                  borderRight: '1px solid #ddd',
                  fontSize: '14px',
                  color: '#262626',
                }}
              >
                <strong>{Math.floor(awardsUsed)}</strong>
              </td>
              <td
                style={{
                  padding: '10px',
                  borderBottom: '1px solid #ddd',
                  borderRight: '1px solid #ddd',
                  fontSize: '14px',
                  color: '#262626',
                }}
              >
                <strong>{Math.floor(awardsCount - awardsUsed)}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* Inline Media Queries for Responsiveness */}
      <div style={{ overflowX: 'auto' }}>
        <Table
          dataSource={nominations}
          columns={columns}
          rowKey="nominationId"
          style={{ overflowX: 'auto' }}
          className="custom-table"
        />
      </div>
      <Modal
        visible={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        centered
        width={700}
        style={{ padding: '10px' }}
      >
        {selectedNomination && (
          <div
            style={{
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '8px',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontFamily: 'Roboto',
                fontWeight: 600,
                fontSize: '16px',
                color: 'rgba(0, 0, 0, 0.88)',
              }}
            >
              {t('reward_details')}
            </h3>
            <Card bordered={false} style={{ marginTop: '16px' }}>
              <Row gutter={[16, 16]} style={{ rowGap: '8px' }}>
                <Col span={12}>
                  <strong>{t('rewards')}</strong>
                  <br /> {selectedNomination.rewardCategory} - {selectedNomination.rewardName}
                </Col>
                <Col span={12}>
                  <strong>{t('nomination_date')}</strong>
                  <br /> {new Date(selectedNomination.nominationDate).toLocaleDateString()}
                </Col>
                <Col span={12}>
                  <strong>{t('month_year')}</strong>
                  <br /> {selectedNomination.nominationMonth} {selectedNomination.nominationYear}
                </Col>
                <Col span={12}>
                  <strong>{t('nominated_by')}</strong>
                  <br /> {selectedNomination.nominatorName || 'N/A'}
                </Col>
                <Col span={12}>
                  <strong>{t('awarded_to')}</strong>
                  <br /> {selectedNomination.nomineeNames?.join(', ') || 'N/A'}
                </Col>
                <Col span={12}>
                  <strong>{t('awarded_points')}</strong>
                  <br /> {selectedNomination.budget} points
                </Col>
                <Col span={12}>
                  <strong>{t('approval_level1')}</strong>
                  <br /> {selectedNomination.approverLevel1.charAt(0).toUpperCase() + selectedNomination.approverLevel1.slice(1)}
                </Col>
                <Col span={12}>
                  <strong>{t('approval_level2')}</strong>
                  <br /> {selectedNomination.approverLevel2.charAt(0).toUpperCase() + selectedNomination.approverLevel2.slice(1)}
                </Col>
                <Col span={12}>
                  <strong>{t('value_demonstrated')}</strong>
                  <br />
                  <Tag
                    style={{
                      backgroundColor: '#8629A4',
                      color: 'white',
                      padding: '4px',
                    }}
                  >
                    {selectedNomination.valueDemonstrated}
                  </Tag>
                </Col>
                {selectedNomination.ecardImage && (
                  <Col span={24}>
                    <div>
                      <strong>{t('ecard')}</strong>
                    </div>
                    <div>
                      <img
                        src={`data:image/jpeg;base64,${selectedNomination.ecardImage}`}
                        alt="E-Card"
                        style={{
                          width: '100%',
                          maxWidth: '300px',
                          height: 'auto',
                          borderRadius: '10px',
                          objectFit: 'cover',
                          marginTop: '10px',
                        }}
                      />
                    </div>
                  </Col>
                )}
                <Col span={24}>
                  <strong>{t('reward_testimonial')}</strong>
                  <br />
                  <Input.TextArea
                    value={extractCitation(selectedNomination.rewardTestimonial)}
                    readOnly
                    style={{
                      width: '100%',
                      height: '80px',
                      resize: 'none',
                    }}
                  />
                </Col>
              </Row>
            </Card>
          </div>
        )}
      </Modal>
      <Modal
        visible={isRejectModalVisible}
        onCancel={handleRejectModalClose}
        footer={null}
        centered
        width={400}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p>{t('reject_nomination')}</p>
          <Button onClick={handleRejectModalClose} style={{ marginRight: '10px' }}>{t('cancel')}</Button>
          <Button type="primary" danger onClick={() => rejectNominationId && handleSubmitNomination(rejectNominationId, 'reject')}>{t('reject')}</Button>
        </div>
      </Modal>
    </div>
  );
};

export default ManagerActionsComponent;